/**
 * ShipmentItemsPanel - Aba Itens da remessa (Logística).
 *
 * Lista itens com o local atual (ShipmentItemsSummary) e inclui em lote pelo
 * ShipmentItemsCatalogModal, somente antes do envio.
 *
 * RELEASE: 2026-10-16 v1.3.0 - Produtos (catálogo e do negócio vinculado) como itens.
 * RELEASE: 2026-10-16 v1.2.0 - Lista e catálogo em modal (padrão Produtos e Serviços).
 * RELEASE: 2026-10-16 v1.1.0 - Oculta itens do prestador destinatário.
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { useShipmentMutations, type ShipmentDetails } from '@/hooks/useShipments';
import { ShipmentItemsCatalogModal, type CatalogPick } from './ShipmentItemsCatalogModal';
import { ShipmentItemsSummary } from './ShipmentItemsSummary';

export { ITEM_LABEL } from './ShipmentItemsSummary';

/** Grava itens escolhidos (cada um revalidado no servidor); devolve quantos falharam. */
export async function addPicks(
  addItem: ReturnType<typeof useShipmentMutations>['addItem'],
  shipmentId: string,
  picks: CatalogPick[],
): Promise<number> {
  let failed = 0;
  for (const p of picks) {
    try {
      await addItem.mutateAsync(p.type === 'free'
        ? { shipmentId, type: 'free', description: p.description, quantity: p.quantity, silent: true }
        : p.type === 'product'
        ? { shipmentId, type: 'product', itemId: p.productId, quantity: p.quantity, silent: true }
        : { shipmentId, type: p.type, itemId: p.itemId, silent: true });
    } catch {
      failed += 1;
    }
  }
  return failed;
}

export function ShipmentItemsPanel({ shipment, editable }: { shipment: ShipmentDetails; editable: boolean }) {
  const { addItem, removeItem } = useShipmentMutations();
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const confirm = async (picks: CatalogPick[]) => {
    setSaving(true);
    const failed = await addPicks(addItem, shipment.id, picks);
    setSaving(false);
    setCatalogOpen(false);
    if (failed) toast.warning(`${failed} item(ns) não foram incluídos.`);
    else toast.success('Itens incluídos');
  };

  return (
    <>
      <ShipmentItemsSummary
        editable={editable}
        removing={removeItem.isPending}
        onAdd={() => setCatalogOpen(true)}
        onRemove={(id) => removeItem.mutate(id)}
        rows={shipment.items.map((i) => ({
          key: i.id, type: i.item_type, label: i.label ?? i.product_name ?? i.description ?? '—',
          place: i.place, serial: !!i.item_id && i.item_type !== 'product', quantity: i.quantity,
          status: i.status ?? null, components: i.components ?? null,
        }))}
      />
      <ShipmentItemsCatalogModal
        open={catalogOpen}
        onOpenChange={setCatalogOpen}
        busy={saving}
        onConfirm={confirm}
        hiddenIds={shipment.items.flatMap((i) => (i.item_id ? [i.item_id] : []))}
        dealId={shipment.links.find((l) => l.entity_type === 'deal')?.entity_id ?? null}
        excludeProviderId={shipment.recipient_is_provider ? shipment.recipient_person_id : null}
      />
    </>
  );
}
